import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import {
  EnvironmentStoreAction,
  StorageNativeStoreAction,
} from "src/components/shared-components/enum/enumStoreAction";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { EnvironmentService } from "src/service/environment.service";
import {
  environmentAccessUserOpenableLoadErrorAction,
  environmentAccessUserOpenableLoadSuccessAction,
  environmentAccessUserOpenableUnsetKeyStoreAction,
  environmentDeviceLoadErrorAction,
  environmentDeviceLoadLastModifiedFromApiAction,
  environmentDeviceLoadListFromApiAction,
  environmentDeviceLoadSuccessAction,
  environmentDeviceUnsetKeyStorageAction,
  environmentLoadErrorAction,
  environmentLoadLastModifiedFromApiAction,
  environmentLoadListFromApiAction,
  environmentLoadSuccessAction,
  environmentUnsetKeyStorageAction,
  environmetAccessControlLoadErrorAction,
  environmetAccessControlLoadSuccessAction,
} from "../actions/environment.actions";
import { currentUserData } from "../selectors/authentication.selectors";
import {
  listEnvironment,
  listEnvironmentDevices,
} from "../selectors/environment.selectors";

/**
 * Handle generic side ngrx effect from environment action
 */
@Injectable()
export class EnvironmentEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store,
    private _environmentService: EnvironmentService
  ) { }

  /**
   * Effect who catch main event and unset specific server key storage
   */
  public environmentUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      map((action) => environmentUnsetKeyStorageAction()),
      map((action) => environmentDeviceUnsetKeyStorageAction()),
      map((action) => environmentAccessUserOpenableUnsetKeyStoreAction())
    )
  );

  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public environmentStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(EnvironmentStoreAction.ENVIRONMENT_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listEnvironment)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const environmentObject: GenericStoreApiData = combinedObject[2];

        if (combinedObject?.data) {
          return environmentLoadLastModifiedFromApiAction({
            environmentObject: environmentObject?.data,
          });
        } else {
          return environmentLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full list of environment effect
   */
  public environmentListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(EnvironmentStoreAction.ENVIRONMENT_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._environmentService.getEnvironments().pipe(
          map((environmentObject: GenericApiData) =>
            environmentLoadSuccessAction({ environmentObject })
          ),
          catchError(() =>
            of(environmentLoadErrorAction({ environmentObject: null }))
          )
        );
      })
    )
  );

  /**
   * Load full list of area by last modified effect
   */
  public environmentListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(EnvironmentStoreAction.ENVIRONMENT_LOAD_LAST_MODIFIED_FROM_API),
      switchMap((action: any) => {
        return this._environmentService
          .getEnvironmentByLastModify(action.environmentObject)
          .pipe(
            map((environmentObject: GenericApiData) =>
              environmentLoadSuccessAction({ environmentObject })
            ),
            catchError(() =>
              of(environmentLoadErrorAction({ environmentObject: null }))
            )
          );
      })
    )
  );

  /**
   * Load full list of environment access user openable
   */
  public environmentAccessUserOpenableLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(
        EnvironmentStoreAction.ENVIRONMENT_ACCESS_USER_OPENABLE_LOAD_FROM_API
      ),
      switchMap((action: any) => {
        return this._environmentService.getAccessesToOpenByUser().pipe(
          map((environmentOpenDoorCommand: GenericApiData) =>
            environmentAccessUserOpenableLoadSuccessAction({
              listEnvironmentOpenDoorCommandObject: environmentOpenDoorCommand,
            })
          ),
          catchError((error) => {
            return of(environmentAccessUserOpenableLoadErrorAction());
          })
        );
      })
    )
  );

  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public environmentDeviceStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(EnvironmentStoreAction.ENVIRONMENT_DEVICE_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listEnvironmentDevices)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const environmentObject: GenericStoreApiData = combinedObject[2];

        if (environmentObject?.data) {
          return environmentDeviceLoadLastModifiedFromApiAction({
            environmentObject: environmentObject.data,
          });
        } else {
          return environmentDeviceLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full list of environment effect
   */
  public environmentDeviceListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(EnvironmentStoreAction.ENVIRONMENT_DEVICE_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._environmentService
          .getEnvironmentWithDeviceRelations()
          .pipe(
            map((environmentObject: GenericApiData) =>
              environmentDeviceLoadSuccessAction({ environmentObject })
            ),
            catchError(() =>
              of(environmentDeviceLoadErrorAction({ environmentObject: null }))
            )
          );
      })
    )
  );

  /**
   * Load full list of area by last modified effect
   */
  public environmentDeviceListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(
        EnvironmentStoreAction.ENVIRONMENT_DEVICE_LOAD_LAST_MODIFIED_FROM_API
      ),
      switchMap((action: any) => {
        return this._environmentService
          .getEnvironmentWithDeviceRelationsByLastModify(
            action.environmentObject
          )
          .pipe(
            map((environmentObject: GenericApiData) =>
              environmentDeviceLoadSuccessAction({ environmentObject })
            ),
            catchError(() =>
              of(environmentDeviceLoadErrorAction({ environmentObject: null }))
            )
          );
      })
    )
  );

  /**
   * Load full list of environment access control
   */
  public environmentAccessControlStartLoadEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(EnvironmentStoreAction.ENVIRONMENT_ACCESS_CONTROL_LOAD_INIT_START),
      switchMap((action: any) => {
        return this._environmentService.getEnvironmentForAccessControl().pipe(
          map((enviromentAccessControlObject: GenericApiData) =>
            environmetAccessControlLoadSuccessAction({
              listEnvironmentAccessControlMaitanance:
                enviromentAccessControlObject,
            })
          ),
          catchError(() => of(environmetAccessControlLoadErrorAction()))
        );
      })
    )
  );
}
